import { useCallback, useEffect, useRef, useState } from 'react';

import { hidePopover } from '../FilterBarV2.types';

import { useAnchorPositionFallback } from './useAnchorPositionFallback';

interface UseFilterPopoverOptions {
	id: string;
}

interface UseFilterPopoverReturn {
	anchorName: string;
	chipWrapperRef: React.RefObject<HTMLDivElement>;
	close: () => void;
	draftVersion: number;
	isOpen: boolean;
	popoverId: string;
	popoverRef: React.RefObject<HTMLDivElement>;
}

export const useFilterPopover = ({ id }: UseFilterPopoverOptions): UseFilterPopoverReturn => {
	const [isOpen, setIsOpen] = useState(false);
	const [draftVersion, setDraftVersion] = useState(0);
	const popoverRef = useRef<HTMLDivElement>(null);
	const chipWrapperRef = useRef<HTMLDivElement>(null);
	const popoverId = `popover-${id}`;
	const anchorName = `--filter-anchor-${id}`;

	// TEMPORARY: Remove this line once we have more adoption of browsers supporting anchors
	useAnchorPositionFallback({ chipWrapperRef, isOpen, popoverRef });

	useEffect(() => {
		chipWrapperRef.current?.querySelector('button')?.setAttribute('popovertarget', popoverId);
	}, [popoverId]);

	const handleToggle = useCallback(() => {
		const opened = popoverRef.current?.matches(':popover-open') ?? false;
		setIsOpen(opened);
		if (opened) setDraftVersion((version) => version + 1);
	}, []);

	useEffect(() => {
		const el = popoverRef.current;
		if (!el) return undefined;
		el.addEventListener('toggle', handleToggle);
		return () => el.removeEventListener('toggle', handleToggle);
	}, [handleToggle]);

	const close = useCallback(() => hidePopover(popoverRef.current), []);

	return { anchorName, chipWrapperRef, close, draftVersion, isOpen, popoverId, popoverRef };
};
